import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Linking, Platform, Text, View } from 'react-native';
import { AppButton } from './AppButton';
import { RequestFormTextField } from './RequestFormField';
import { useAppColors } from '../context/PreferencesContext';
import { normalizeApiError } from '../api/apiError';
import {
  addAttendanceComment,
  getAttendanceComments,
  type AttendanceComments as Comments,
} from '../services/attendanceCommentService';
import {
  captureCommentLocation,
  CommentLocationError,
  type CommentLocationFailure,
} from '../utils/commentLocation';
import { spacing } from '../constants/spacing';
import { formatDateTime } from '../utils/dateTime';
import { runOperation } from '../utils/operationFeedback';

export function AttendanceComments({
  attendanceId,
  canMark,
  disabled,
  refreshKey,
  showList = true,
  onAdded,
}: {
  attendanceId: number;
  canMark: boolean;
  disabled: boolean;
  refreshKey: string;
  /** False when the caller renders the comments itself (e.g. tracking timeline). */
  showList?: boolean;
  onAdded?: () => void;
}) {
  const palette = useAppColors();
  const [data, setData] = useState<Comments | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [locationFailure, setLocationFailure] =
    useState<CommentLocationFailure | null>(null);
  const [busy, setBusy] = useState(false);
  const submitting = useRef(false);
  const mounted = useRef(true);
  const load = useCallback(async () => {
    try {
      const next = await getAttendanceComments(attendanceId);
      if (mounted.current) {
        setData(next);
        setError(null);
      }
    } catch (caught) {
      if (mounted.current) {
        setLocationFailure(null);
        setError(normalizeApiError(caught).message);
      }
    }
  }, [attendanceId]);
  useEffect(() => {
    mounted.current = true;
    void load();
    return () => {
      mounted.current = false;
    };
  }, [load, refreshKey]);

  async function save() {
    if (submitting.current || disabled || !data?.canAdd || !canMark) return;
    if (!draft.trim()) {
      setError('Enter a comment.');
      return;
    }
    if (draft.trim().length > 1000) {
      setError('Comment must not exceed 1000 characters.');
      return;
    }
    submitting.current = true;
    setBusy(true);
    setError(null);
    setLocationFailure(null);
    try {
      const location = await captureCommentLocation();
      if (!mounted.current) return;
      const comment = await runOperation('add', () =>
        addAttendanceComment(attendanceId, {
          comment: draft.trim(),
          ...location,
        }),
      );
      if (!mounted.current) return;
      setData(current =>
        current ? { ...current, items: [...current.items, comment] } : current,
      );
      setDraft('');
      setEditing(false);
      onAdded?.();
    } catch (caught) {
      if (!mounted.current) return;
      setLocationFailure(
        caught instanceof CommentLocationError ? caught.reason : null,
      );
      setError(
        caught instanceof Error
          ? caught.message
          : normalizeApiError(caught).message,
      );
    } finally {
      submitting.current = false;
      if (mounted.current) setBusy(false);
    }
  }

  if (data?.eligible === false) return null;
  return (
    <View style={{ gap: spacing.md }}>
      {data?.canAdd && canMark && !editing ? (
        <AppButton
          title="Add Comment"
          variant="secondary"
          disabled={disabled}
          onPress={() => {
            setEditing(true);
            setError(null);
          }}
        />
      ) : null}
      {editing ? (
        <>
          <RequestFormTextField
            label="Comment"
            value={draft}
            onChangeText={setDraft}
            multiline
            editable={!busy && !disabled}
          />
          <AppButton
            title={busy ? 'Getting location and saving…' : 'Save Comment'}
            disabled={busy || disabled || !draft.trim() || !data?.canAdd}
            onPress={save}
          />
          <AppButton
            title="Cancel"
            variant="secondary"
            disabled={busy}
            onPress={() => {
              setEditing(false);
              setError(null);
              setLocationFailure(null);
            }}
          />
        </>
      ) : null}
      {error ? (
        <>
          <Text accessibilityRole="alert" style={{ color: palette.error }}>
            {error}
          </Text>
          {locationFailure === 'blocked' ||
          (locationFailure === 'disabled' && Platform.OS === 'android') ? (
            <AppButton
              title="Open Settings"
              variant="secondary"
              onPress={() => {
                void (locationFailure === 'disabled'
                  ? Linking.sendIntent(
                      'android.settings.LOCATION_SOURCE_SETTINGS',
                    )
                  : Linking.openSettings()
                ).catch(() => Linking.openSettings());
              }}
            />
          ) : null}
          {!editing ? (
            <AppButton
              title="Retry Comments"
              variant="secondary"
              onPress={load}
            />
          ) : null}
        </>
      ) : null}
      {showList && data?.items.map(item => (
        <View key={item.commentId} style={{ gap: spacing.xs }}>
          <Text style={{ color: palette.textPrimary }}>{item.comment}</Text>
          <Text style={{ color: palette.textSecondary }}>
            Latitude: {item.latitude}, Longitude: {item.longitude}
          </Text>
          <Text style={{ color: palette.textMuted }}>
            {formatDateTime(item.createdAt)}
          </Text>
        </View>
      ))}
    </View>
  );
}
